import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { NotificationService } from '@services/notification.service';
import { RefDataService } from '@services/ref-data.service';
import { forkJoin } from 'rxjs';
import { Domain, Language, Market } from '@shared/models/dataref.model';
import { RecipeService } from '@services/recipe/recipe.service';

@Component({
  selector: 'app-recipes-copy-by-import',
  templateUrl: './recipes-copy-by-import.component.html',
  styleUrls: ['./recipes-copy-by-import.component.scss'],
  standalone: false,
})
export class RecipesCopyByImportComponent implements OnInit {
  formImport = this.formBuilder.group({});
  domains: Domain[] = [];
  markets: Market[] = [];
  languages: Language[] = [];
  languagesFilteredSources: Language[] = [];
  languagesFilteredTarget: Language[] = [];
  fileName = '';
  fileErrorExtension = false;
  fileErrorFormat = false;

  constructor(
    private readonly refDataService: RefDataService,
    private readonly recipeService: RecipeService,
    private readonly notificationService: NotificationService,
    private readonly formBuilder: UntypedFormBuilder,
  ) {}

  ngOnInit(): void {
    const queries = [this.refDataService.getByKey('domains'), this.refDataService.getByKey('languages')];
    forkJoin(queries).subscribe((responses) => {
      this.domains = this.refDataService.translateReference(this.refDataService.toArray(responses[0]));
      this.languages = this.refDataService.translateReference(this.refDataService.toArray(responses[1]));
    });
    this.formImport = this.formBuilder.group({
      ids: [[], Validators.required],
      sourceDomain: ['', Validators.required],
      sourceMarket: ['', Validators.required],
      sourceLang: ['', Validators.required],
      targetDomain: ['', Validators.required],
      targetMarket: ['', Validators.required],
      targetLang: ['', Validators.required],
    });
  }

  reset(): void {
    this.formImport.reset();
    this.fileName = '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getName(key: any, list: any): any {
    const obj: any = (list ?? []).find((object: any) => object.key === key);
    if (obj) {
      return obj.translatedName;
    }
    return '';
  }

  submit(): void {
    if (this.formImport.invalid || this.fileErrorExtension || this.fileErrorFormat) {
      return;
    }
    this.recipeService.copyByImport(this.formImport.value).subscribe(
      () => {
        this.notificationService.sendSuccess('COPY_RECIPES_MARKET.MODAL.MSG.SUCCESS');
        this.reloadPage();
      },
      (err) => this.errorManager(err),
    );
  }

  reloadPage(): void {
    location.reload();
  }

  updateLang(currentMarket: string, isSource: boolean): void {
    const market = this.markets.find((m) => m.key === currentMarket);
    if (market !== undefined) {
      const languagesFiltered = this.refDataService.translateReference(Object.values(market.langs));
      if (isSource) {
        this.languagesFilteredSources = languagesFiltered;
        if (this.languagesFilteredSources.length === 1) {
          this.formImport.controls.sourceLang.setValue(this.languagesFilteredSources[0].key);
        } else {
          this.formImport.controls.sourceLang.reset('');
        }
      } else {
        this.languagesFilteredTarget = languagesFiltered;
        if (this.languagesFilteredTarget.length === 1) {
          this.formImport.controls.targetLang.setValue(this.languagesFilteredTarget[0].key);
        } else {
          this.formImport.controls.targetLang.reset('');
        }
      }
    }
  }

  updateDomain(domain: string): void {
    this.formImport.controls.targetDomain.setValue(domain);
    this.formImport.controls.sourceMarket.setValue('');
    this.formImport.controls.sourceLang.setValue('');
    this.formImport.controls.targetMarket.setValue('');
    this.formImport.controls.targetLang.setValue('');
    this.refDataService.getByKey('markets', false, domain).subscribe((markets) => {
      this.markets = this.refDataService.translateReference(this.refDataService.toArray(markets));
    });
  }

  onFileSelected(inputNode: HTMLInputElement): void {
    if (typeof FileReader !== 'undefined') {
      const reader = new FileReader();

      reader.onload = () => {
        this.processFile(reader.result as string);
      };

      if (inputNode.files) {
        this.fileName = inputNode.files[0].name;
        this.fileErrorExtension = !this.fileName.toLowerCase().endsWith('.csv');
        reader.readAsText(inputNode.files[0]);
      }
    }
  }

  processFile(fileTxt: string): void {
    const allTextLines = fileTxt?.split(/\r|\n|\r/);

    let separator = '';
    separator = allTextLines[0].includes(',') ? ',' : separator;
    separator = allTextLines[0].includes(';') ? ';' : separator;
    separator = allTextLines[0].includes(':') ? ':' : separator;
    // Headings
    const headers = allTextLines[0].split(separator);
    const indexId = headers.indexOf('recipeId');
    const indexSourceSystem = headers.indexOf('_recipeIdentifierSourceSystem');

    this.fileErrorFormat = indexId === -1 || indexSourceSystem === -1;
    // Rows
    const idsList = [];
    const arrl = allTextLines.length;
    for (let i = 1; i < arrl; i++) {
      const row = allTextLines[i].split(separator);
      if (row[indexId]) {
        idsList.push({ functionalId: row[indexId], sourceSystem: { key: row[indexSourceSystem] } });
      }
    }
    this.formImport.controls.ids.setValue(idsList);
  }

  private errorManager(err: any): void {
    if (err.status && err.status === 403) {
      this.notificationService.sendError('COPY_RECIPES_MARKET.MODAL.MSG.ERROR.FORBIDDEN');
    } else if (err.error?.errorCode && err.error.errorCode === '28015') {
      const errorMessage = err.error.message.split(':');
      const recipesNotFound = errorMessage[errorMessage.length - 1].trim();
      if (recipesNotFound.split(',').length === 1) {
        this.notificationService.sendError('COPY_RECIPES_MARKET.MODAL.MSG.ERROR.NOT_FOUND', { recipes: recipesNotFound });
      } else {
        this.notificationService.sendError('COPY_RECIPES_MARKET.MODAL.MSG.ERROR.NOT_FOUND_MULTIPLE', { recipes: recipesNotFound });
      }
    } else {
      this.notificationService.sendAPIError(err, 'COPY_RECIPES_MARKET.MODAL.MSG.ERROR.DEFAULT');
    }
  }
}
